import React, { useEffect, useMemo, useState } from 'react';

import { LOCK } from '../icons';
import { getIntakePrivate } from '../../services/records';
import { canReadPrivateAnswers } from '../../utils/intakeLinks';
import { normalizeId } from '../../utils/ids';
import { elementLabel, formatValue } from './itemValueDisplay';

/**
 * What a public form's visitor sent PRIVATELY with this submission (KPR-389;
 * keepr-api docs/SCHEMA.md § Private answers): the answers to the link's
 * private questions — typically a reporter's name and phone number — which are
 * never part of the record. Shown beside the submission, to the people who
 * manage its collection and nobody else.
 *
 * It asks only when it could be answered: the item came through a public form
 * (`item.intake`), it lives in `collection` (a linked row read from another
 * collection is not this collection's to explain), and the reader's OWN role
 * there is manage or owner — the API answers from the caller's own grants and
 * gives an admin no way in. Anything else renders nothing, and so does a
 * submission with nothing private (404) — the panel never says "nothing was
 * sent privately" to someone who might not have been allowed to see it.
 *
 * Values are printed as text through the shared formatter — never as a link
 * or a filter door: a click that filtered the list by a reporter's phone
 * number would put it in the address bar.
 *
 * KPR-459 (keepr-api, element grants G8): with the server's element rules on,
 * the answers are ON the record, held by a private question's element rule —
 * a manager reads them as the record's own see-only fields, and the API says
 * `onRecord: true`. The panel then renders nothing: showing them twice would
 * say they are two things. `onRecord: false` is a submission whose answers
 * are still kept apart (sent before the rules were on); the panel shows them,
 * saying so. No `onRecord` at all is the API with the rules off — the panel
 * as it always was.
 */
export default function IntakePrivatePanel({ item, collection, elementDefs = [], className = '' }) {
	const itemId = normalizeId(item?._id);
	const inCollection = Boolean(collection) && normalizeId(item?.collection_id) === normalizeId(collection?._id);
	const asks = Boolean(item?.intake) && inCollection && canReadPrivateAnswers(collection) && Boolean(itemId);
	const [answers, setAnswers] = useState(null);

	useEffect(() => {
		setAnswers(null);
		if (!asks) return undefined;
		let live = true;
		getIntakePrivate(itemId)
			.then((data) => { if (live) setAnswers(data); })
			.catch(() => { if (live) setAnswers(null); });
		return () => { live = false; };
	}, [asks, itemId]);

	const rows = useMemo(() => {
		const elements = answers?.elements && typeof answers.elements === 'object' ? answers.elements : null;
		if (!elements) return [];
		const byName = new Map((elementDefs || []).filter(def => def?.name).map(def => [def.name, def]));
		return Object.keys(elements).map((name) => {
			// An element since removed from the card still shows, by its name.
			const def = byName.get(name) || { name, dataType: 'text-small' };
			const text = formatValue(def, elements[name]);
			return { name, label: elementLabel(def) || name, text: text === '' || text === null || text === undefined ? String(elements[name]) : String(text) };
		});
	}, [answers, elementDefs]);

	if (!asks || !rows.length || answers?.onRecord === true) return null;
	// Kept apart while the rules are on (KPR-459): kept, never deleted with the
	// record — the switch-off lifecycle sentence below would be wrong.
	const apartWhileRulesOn = answers?.onRecord === false;

	return (
		<section
			className={['intake-private', className].filter(Boolean).join(' ')}
			aria-labelledby={`intake-private-${itemId}`}
			data-testid="intake-private-panel"
		>
			<h3 className="intake-private__title" id={`intake-private-${itemId}`}>
				<LOCK aria-hidden="true" className="intake-private__icon" />
				Sent privately with this submission
			</h3>
			<p className="intake-private__hint">
				{apartWhileRulesOn
					? 'Only people who manage this collection see these answers. They are kept apart from the record until they are moved onto it, where only the managers can see them.'
					: 'Only people who manage this collection see these answers. They are not part of the record, and they are deleted for good if the record is rejected, deleted or moved to another collection.'}
			</p>
			<dl className="intake-private__list">
				{rows.map(row => (
					<div className="intake-private__row" key={row.name}>
						<dt className="micro-label">{row.label}</dt>
						<dd className="intake-private__value">{row.text}</dd>
					</div>
				))}
			</dl>
		</section>
	);
}
